import { ECurrency, ELanguage, ESalutation } from '@ringana/configs';
import config from '../../../config';
import {
  IShoppingCart,
  IShoppingCartIndividual,
  IShoppingCartItem, IShoppingCartSupplement,
} from '../../common/interfaces/cart.interface';
import ApplicationError from '../../errors/application-error';
import { EErrorMessage } from '../../common/enums/messages.enum';
import {
  IPartnerApplication, IPartnerApplicationBankInformation, IPartnerApplicationBaseData,
  IPartnerApplicationNeededProductData,
  IPartnerApplicationProductMatchcodes, IPartnerApplicationProvision, IPartnerApplicationSocialMediaData,
  IPartnerApplicationStep1,
  IPartnerApplicationStep2, IPartnerApplicationStep3, IPartnerApplicationStep4,
} from '../../common/interfaces/partner-application.interface';
import { IProduct, IWPProduct } from '../../common/interfaces/products.interface';
import OldFormatHelper from '../OldFormatHelper';
import { ICustomer } from '../../common/interfaces/customers.interface';
import { EPhoneType } from '../../common/enums/customers.enum';
import CookieHelper from '../OldCookieHelper';
import { ICustomSelectOption } from '../../common/interfaces/shared.interface';
import AppHelper from '../AppHelper';

export default class PartnerApplicationHelper {
  private static initialBaseData = {
    cartId: ``,
    deliveryAddress: 0,
    paymentMethodId: null,
  }

  private static blankNeededProductInfo = {
    additionalField: ``,
    additionalFields: ``,
    cartContentId: 0,
    image: ``,
    matchcode: ``,
    price: ``,
    provisionPoints: ``,
    selected: false,
    shortinfo: ``,
    title: ``,
  }

  private static initialStep1: IPartnerApplicationStep1 = {
    basicLanguage: `de`,
    basicProductData: PartnerApplicationHelper.blankNeededProductInfo,
    basicProductSelected: false,
    basicProductVisible: false,
    index: 1,
    nextStep: 2,
    previousStep: 0,
    starterProductData: PartnerApplicationHelper.blankNeededProductInfo,
    starterProductSelected: false,
    starterProductVisible: false,
  }

  private static initialStep2: IPartnerApplicationStep2 = {
    basicLanguage: `de`,
    bonusProducts: [],
    index: 2,
    nextStep: 3,
    previousStep: 1,
    productsVisible: false,
    selectedFabProduct: `FB-FB-BP1`,
    selectedFdLanguage: `de`,
    selectedProductMatchcodes: [],
  }

  private static initialStep3: IPartnerApplicationStep3 = {
    bankInformation: {
      sepa: false,
      swift: false,
    },
    basicLanguage: `de`,
    furtherActivitiesData: {
      furtherActivities: [],
      hasFurtherActivities: false,
    },
    index: 3,
    nextStep: 4,
    previousStep: 2,
    socialMediaData: {
      hasSocialMedia: false,
      socialMediaAccounts: [],
    },
  }

  private static initialStep4: IPartnerApplicationStep4 = {
    basicLanguage: `de`,
    confirmationLine1: false,
    confirmationLine2: false,
    index: 4,
    nextStep: 5,
    previousStep: 3,
  }

  private static initialPartnerApplication: IPartnerApplication = {
    addresses: [],
    birthName: null,
    birthPlace: null,
    cartId: -1,
    company: null,
    deliveryType: {
      cost: 0,
      id: -1,
      name: ``,
      service: ``,
    },
    invoiceAddress: {
      city: ``,
      deliveryDefault: false,
      receiptDefault: false,
      recipient: ``,
      street: ``,
      streetNumber: ``,
      zipCode: ``,
    },
    nationality: null,
    partnerNumber: ``,
    paymentTerms: [],
    provisionType: null,
    sepa: false,
    socialMedia: [],
    socialSecurityNumber: null,
    taxNumber: ``,
    taxNumberIsVerified: null,
    uid: null,
  };

  private static initialProductMatchcodes: IPartnerApplicationProductMatchcodes = {
    base: `O-STS`,
    bonusFreshDate: `FB-FD`,
    bonusFreshLight: `FB-FB-BP1`,
    bonusFreshMedium: `FB-FB-BP2`,
    bonusFreshRich: `FB-FB-BP3`,
    bonusSport: `FB-SPORT`,
    bonusSupplements: `STS-V`,
    starter: `O2-STS`,
  }

  private static initialCustomerData: ICustomer = {
    account: {
      customerNumber: -1,
      mail: ``,
    },
    changedEmail: false,
    currency: ECurrency.EUR,
    isPartner: false,
    language: ELanguage.DE,
    partnerData: {
      companyBookingNumber: ``,
      extra: ``,
      image: ``,
      newsletterUnsubscribed: false,
      socialSecurityNumber: ``,
      uid: `string`,
    },
    personalInfo: {
      address: {
        city: ``,
        country: ``,
        deliveryDefault: false,
        postalCode: ``,
        receiptDefault: false,
        recipient: ``,
        street: ``,
        streetNumber: ``,
        useTaxNumber: false,
        zipCode: ``,
      },
      birthdate: ``,
      company: ``,
      firstName: ``,
      lastName: ``,
      newsletter: false,
      partner: false,
      partnerNumber: ``,
      phone: {
        code: ``,
        country: ``,
        number: ``,
        type: EPhoneType.LANDLINE,
      },
      salutation: ESalutation.MR,
      taxNumber: ``,
      taxNumberIsVerified: false,
    },
  }

  private static provisionObject: IPartnerApplicationProvision = {
    fr: {
      data: [
        {
          id: `vdi`,
          label: `contractVDIFR`,
          value: `fr_vdi`,
        },
        {
          id: `mp`,
          label: `contractMPFR`,
          value: `fr_mandataire_professionnel`,
        },
      ],
      needed: false,
    },
    it: {
      needed: false,
      value: `it_portaporta`,
    },
  };

  private static initialSocialMediaData: IPartnerApplicationSocialMediaData = {
    hasSocialMedia: false,
    socialMediaAccounts: [],
  }

  private static socialMediaTypes: ICustomSelectOption[] = [
    { optionText: `Facebook`, optionValue: `3` },
    { optionText: `Instagram`, optionValue: `4` },
    { optionText: `Youtube`, optionValue: `5` },
    { optionText: `Pinterest`, optionValue: `6` },
    { optionText: `Sonstige`, optionValue: `7` },
  ];

  public static getInitialBaseData(): IPartnerApplicationBaseData {
    return this.initialBaseData;
  }

  public static getInitialStep1(): IPartnerApplicationStep1 {
    return this.initialStep1;
  }

  public static getInitialStep2(): IPartnerApplicationStep2 {
    const clonedStep2 = AppHelper.deepClone(this.initialStep2);
    if(window?.navigator?.language) {
      clonedStep2.selectedFdLanguage = window.navigator.language.substr(0, 2);
    }
    return clonedStep2;
  }

  public static getInitialStep3(): IPartnerApplicationStep3 {
    return this.initialStep3;
  }

  public static getInitialStep4(): IPartnerApplicationStep4 {
    return this.initialStep4;
  }

  public static getBlankNeededProductInfo(): IPartnerApplicationNeededProductData {
    return this.blankNeededProductInfo;
  }

  public static getInitialPartnerApplication(): IPartnerApplication {
    return this.initialPartnerApplication;
  }

  public static getInitialProductMatchcodes(): IPartnerApplicationProductMatchcodes {
    return this.initialProductMatchcodes;
  }

  public static getInitialCustomerData(): ICustomer {
    return this.initialCustomerData;
  }

  public static getProvisionObject(VDIFR: string, MPFR: string): IPartnerApplicationProvision {
    const clonedProvisionObject = AppHelper.deepClone(this.provisionObject);
    clonedProvisionObject.fr.data[0].label = VDIFR;
    clonedProvisionObject.fr.data[1].label = MPFR;
    return clonedProvisionObject;
  }

  public static getInitialSocialMediaData(): IPartnerApplicationSocialMediaData {
    return this.initialSocialMediaData;
  }

  public static getSocialMediaTypes(): ICustomSelectOption[] {
    return this.socialMediaTypes;
  }

  public static isProductABaseProduct(matchcode: string): boolean {
    let response = false;
    window.vuePartnerApplication.baseMatchCodes.forEach((code: string) => {
      if(matchcode.includes(code)) {
        response = true;
      }
    });
    return response;
  }

  public static findPartnerApplicationCart(data: IShoppingCart): IShoppingCartIndividual {
    if(!data) {
      throw new ApplicationError(EErrorMessage.VALIDATION_FAILED);
    }
    const shoppingCart = data.shoppingCarts.find(cart => cart.cartTypeId === config.cartTypes.PARTNERAPPLICATION);
    if(!shoppingCart) {
      throw new ApplicationError(EErrorMessage.NOT_FOUND);
    }
    return shoppingCart;
  }

  public static getNeededProductData(wpProduct: IWPProduct, apiProduct: IProduct, currency: string): IPartnerApplicationNeededProductData {
    return {
      additionalField: (undefined !== wpProduct.additionalField && wpProduct.additionalField !== null) ? wpProduct.additionalField : ``,
      additionalFields: (undefined !== wpProduct.additionalFields && wpProduct.additionalFields !== null) ? wpProduct.additionalFields : ``,
      cartContentId: 0,
      image: (undefined !== wpProduct.image && wpProduct.image !== null) ? wpProduct.image : ``,
      matchcode: (undefined !== wpProduct.matchcode && wpProduct.matchcode !== null) ? wpProduct.matchcode : ``,
      price: (undefined !== apiProduct.prices.partner.brutto && apiProduct.prices.partner.brutto !== null) ? OldFormatHelper.getPrice(apiProduct.prices.partner.brutto, currency) : ``,
      provisionPoints: (undefined !== apiProduct.prices.partner.provisionPoints && apiProduct.prices.partner.provisionPoints !== null) ? apiProduct.prices.partner.provisionPoints : ``,
      selected: false,
      shortinfo: (undefined !== wpProduct.shortinfo && wpProduct.shortinfo !== null) ? wpProduct.shortinfo : ``,
      title: (undefined !== wpProduct.title && wpProduct.title !== null) ? wpProduct.title : ``,
    };
  }

  public static findCartItemCorrespondingToProductVariantMatchcode(matchCode: string, cart: IShoppingCartIndividual): IShoppingCartItem | null {
    const items = this.getItemsWithVariants(cart);
    for(const item of items) {
      if(item.availableVariants.find(variant => variant.matchcode === matchCode)) {
        return item;
      }
    }
    return null;
  }

  private static getItemsWithVariants(cart: IShoppingCartIndividual): IShoppingCartItem[] {
    return cart.items
      .map(item => (item.availableVariants.length > 0 ? item : undefined))
      .filter(item => !!item) as IShoppingCartItem[];
  }

  public static isNotBaseProduct(item: IShoppingCartItem | string, baseProducts: IPartnerApplicationProductMatchcodes): boolean {
    let matchCodeToCheck: string;
    if(typeof item !== `string`) {
      matchCodeToCheck = item.matchcode;
    } else {
      matchCodeToCheck = item;
    }
    for(const prod of Object.keys(baseProducts)) {
      const code = baseProducts[prod as keyof IPartnerApplicationProductMatchcodes];
      if(new RegExp(`^${code}`).test(matchCodeToCheck)) {
        return false;
      }
    }
    return true;
  }

  public static getCartPrice(price: number): string {
    return OldFormatHelper.getPrice(price, CookieHelper.getCurrency());
  }

  public static getCartPoints(points: number): string {
    return OldFormatHelper.format(points);
  }

  public static createNeededBankData(bankInformation: IPartnerApplicationBankInformation): string[] {
    const neededBankData = [];
    if(bankInformation.sepa || bankInformation.swift) {
      neededBankData.push(`bankBic`);
      neededBankData.push(`bankAccountHolder`);
    }

    if(bankInformation.sepa) {
      neededBankData.push(`iban`);
      neededBankData.push(`bankName`);
    }

    if(bankInformation.swift) {
      neededBankData.push(`bankAccountNumber`);
    }

    return neededBankData;
  }

  public static getBaseMatchCode(supplement: IShoppingCartSupplement): string {
    let baseMatchcode = ``;
    switch(supplement.supplementId) {
      case 49:
      case 57:
      case 58:
      case 59:
      case 66:
      case 67:
      case 89:
      case 90:
      case 91:
        baseMatchcode = `RPG`;
        break;
      case 28:
      case 29:
      case 30:
      case 31:
      case 32:
      case 45:
        baseMatchcode = `RPOE`;
        break;
      case 96:
      case 97:
      case 98:
      case 99:
      case 100:
      case 101:
      case 102:
      case 103:
      case 104:
        baseMatchcode = `RPGC`;
        break;
      case 1:
      case 3:
      case 4:
      case 5:
      case 6:
      case 44:
      case 46:
      case 92:
      case 94:
      case 95:
      case 118:
        baseMatchcode = `RM`;
        break;
      default:
    }
    return baseMatchcode;
  }
}
